<template>
  <Dialog :visible="visible" :header="$t('DevTipsResultsOverlay.title')"
    :style="{ width: '90vw', maxWidth: '800px' }"
     :modal="true" :closable="true" :closeOnEscape="true"
    @update:visible="$emit('update:visible', $event)"
    class="dev-tips-results-overlay">
    <div v-if="results" class="results-container">
      <!-- Summary Cards -->
      <div class="summary-cards">
        <div class="summary-card success">
          <div class="card-icon">
            <i class="pi pi-users"></i>
          </div>
          <div class="card-content">
            <div class="card-value">{{ results.usersProcessed }}</div>
            <div class="card-label">{{ $t('DevTipsResultsOverlay.usersProcessed') }}</div>
          </div>
        </div>

        <div class="summary-card primary">
          <div class="card-icon">
            <i class="pi pi-lightbulb"></i>
          </div>
          <div class="card-content">
            <div class="card-value">{{ results.tipsRecommended }}</div>
            <div class="card-label">{{ $t('DevTipsResultsOverlay.tipsRecommended') }}</div>
          </div>
        </div>

        <div class="summary-card warning" v-if="results.errors && results.errors.length > 0">
          <div class="card-icon">
            <i class="pi pi-exclamation-triangle"></i>
          </div>
          <div class="card-content">
            <div class="card-value">{{ results.errors.length }}</div>
            <div class="card-label">{{ $t('DevTipsResultsOverlay.errors') }}</div>
          </div>
        </div>
      </div>

      <!-- Schema Info -->
      <div class="schema-info">
        <h3>{{ $t('DevTipsResultsOverlay.schemaInfo') }}</h3>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">{{ $t('DevTipsResultsOverlay.schemaName') }}:</span>
            <span class="info-value">{{ results.schemaName }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('DevTipsResultsOverlay.schemaType') }}:</span>
            <span class="info-value">{{ results.schemaType }}</span>
          </div>
        </div>
      </div>

      <!-- User Results -->
      <div class="user-results" v-if="results.userResults && results.userResults.length > 0">
        <h3>{{ $t('DevTipsResultsOverlay.userResults') }}</h3>
        <div class="user-list">
          <div v-for="(userResult, index) in results.userResults"
           :key="index" class="user-result-item">
            <div class="user-header">
              <div class="user-id">{{
                 $t('DevTipsResultsOverlay.userId') }}: {{ userResult.userId }}</div>
              <div class="user-date">{{ formatDate(userResult.assessmentDate) }}</div>
            </div>

            <div class="recommendation-details" v-if="userResult.recommendation">
              <div class="dimension-info">
                <span class="dimension-label">{{
                   $t('DevTipsResultsOverlay.targetDimension') }}:</span>
                <span class="dimension-value">{{
                 userResult.recommendation.selectedDimension }}</span>
                <span class="dimension-score">({{
                 userResult.recommendation.dimensionScore.toFixed(2) }})</span>
              </div>

              <div class="tip-info">
                <div class="tip-title">{{ userResult.recommendation.tip.title.en ||
                  userResult.recommendation.tip.title.de }}</div>
                <div class="tip-text">{{
                 userResult.recommendation.tip.text.en || userResult.recommendation.tip.text.de
                  }}</div>
              </div>

              <div class="reason-badge" :class="getReasonClass(userResult.recommendation.reason)">
                {{ getReasonText(userResult.recommendation.reason) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Errors -->
      <div class="errors-section" v-if="results.errors && results.errors.length > 0">
        <h3>{{ $t('DevTipsResultsOverlay.errors') }}</h3>
        <div class="error-list">
          <div v-for="(error, index) in results.errors" :key="index" class="error-item">
            <i class="pi pi-exclamation-triangle error-icon"></i>
            <span class="error-text">{{ error.error }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else-if="loading" class="loading-container">
      <ProgressSpinner />
      <p>{{ $t('DevTipsResultsOverlay.processing') }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <i class="pi pi-exclamation-triangle error-icon-large"></i>
      <h3>{{ $t('DevTipsResultsOverlay.errorTitle') }}</h3>
      <p>{{ error }}</p>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="footer-actions">
        <Button :label="$t('DevTipsResultsOverlay.close')"
         @click="closeOverlay" class="p-button-secondary" />
        <Button v-if="results && results.userResults && results.userResults.length > 0"
          :label="$t('DevTipsResultsOverlay.exportResults')"
           @click="exportResults" icon="pi pi-download"
          class="p-button-primary" />
      </div>
    </template>
  </Dialog>
</template>

<script>
import { defineComponent } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';

export default defineComponent({
  name: 'DevTipsResultsOverlay',
  components: {
    Dialog,
    Button,
    ProgressSpinner,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    results: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
  },
  emits: ['close', 'update:visible'],
  methods: {
    closeOverlay() {
      this.$emit('close');
      this.$emit('update:visible', false);
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString();
    },
    getReasonClass(reason) {
      return {
        'reason-improvement': reason === 'improvement_needed',
        'reason-opportunity': reason === 'development_opportunity',
      };
    },
    getReasonText(reason) {
      const reasons = {
        improvement_needed: this.$t('DevTipsResultsOverlay.reasonImprovement'),
        development_opportunity: this.$t('DevTipsResultsOverlay.reasonOpportunity'),
      };
      return reasons[reason] || reason;
    },
    exportResults() {
      if (!this.results) return;

      const data = {
        timestamp: new Date().toISOString(),
        schema: {
          name: this.results.schemaName,
          type: this.results.schemaType,
        },
        summary: {
          usersProcessed: this.results.usersProcessed,
          tipsRecommended: this.results.tipsRecommended,
          errors: this.results.errors?.length || 0,
        },
        userResults: this.results.userResults || [],
        errors: this.results.errors || [],
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `devtips-results-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
});
</script>

<style scoped>
.dev-tips-results-overlay {
  font-family: 'Inter', sans-serif;
}

.results-container {
  max-height: 70vh;
  overflow-y: auto;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.summary-card {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
}

.summary-card.success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.summary-card.primary {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}

.summary-card.warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.card-icon {
  font-size: 2rem;
  margin-right: 1rem;
}

.card-value {
  font-size: 2rem;
  font-weight: bold;
  line-height: 1;
}

.card-label {
  font-size: 0.875rem;
  opacity: 0.9;
  margin-top: 0.25rem;
}

.schema-info {
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
}

.schema-info h3 {
  margin: 0 0 1rem 0;
  color: #1e293b;
  font-size: 1.125rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-label {
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 0.25rem;
}

.info-value {
  font-weight: 600;
  color: #1e293b;
}

.user-results {
  margin-bottom: 2rem;
}

.user-results h3 {
  margin: 0 0 1rem 0;
  color: #1e293b;
  font-size: 1.125rem;
}

.user-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.user-result-item {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.user-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.user-id {
  font-weight: 600;
  color: #1e293b;
}

.user-date {
  font-size: 0.875rem;
  color: #64748b;
}

.recommendation-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.dimension-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dimension-label {
  font-size: 0.875rem;
  color: #64748b;
}

.dimension-value {
  font-weight: 600;
  color: #1e293b;
}

.dimension-score {
  font-size: 0.875rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.tip-info {
  background: #f8fafc;
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid #3b82f6;
}

.tip-title {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.tip-text {
  color: #475569;
  line-height: 1.5;
}

.reason-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  width: fit-content;
}

.reason-improvement {
  background: #fef3c7;
  color: #92400e;
}

.reason-opportunity {
  background: #dbeafe;
  color: #1e40af;
}

.errors-section {
  margin-top: 2rem;
}

.errors-section h3 {
  margin: 0 0 1rem 0;
  color: #dc2626;
  font-size: 1.125rem;
}

.error-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.error-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
}

.error-icon {
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.error-text {
  line-height: 1.5;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  text-align: center;
}

.error-icon-large {
  font-size: 3rem;
  color: #dc2626;
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

@media (max-width: 768px) {
  .summary-cards {
    grid-template-columns: 1fr;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .user-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .footer-actions {
    flex-direction: column;
  }
}
</style>
